/**
 * «هنوز دوستی نداری» — یه‌بار، درست قبل از ساختنِ اولین سیگنال.
 *
 * سیگنال برای دوستان فرستاده می‌شه. کسی که هیچ‌کس رو اضافه نکرده می‌تونه
 * فرمِ کامل رو پر کنه و بفرسته، و چیزی که ساخته هیچ‌جا دیده نمی‌شه — نه چون
 * خطایی داده، فقط چون گیرنده‌ای نیست. پس **قبل از** باز شدنِ فرم می‌پرسیم:
 * ادامه بدم، یا بریم صفحه‌ی دوستان؟
 *
 * دو چیز عمدی این‌جا:
 *
 * ۱. **جلوش گرفته نمی‌شه.** «ادامه» یه گزینه‌ی واقعیه؛ سیگنالِ بی‌گیرنده لینکِ
 *    عمومی داره و کاملاً کارِ درستی می‌تونه باشه (نگاه کن به noFriendsYetCreate
 *    توی فرمِ ساخت، که همین رو می‌گه).
 * ۲. **یه‌بار برای هر کاربر.** هشداری که هر بار بیاد، بعدِ دومین بار دیگه
 *    خونده نمی‌شه و فقط یه لمسِ اضافه‌ست. پرچمش روی همین دستگاه می‌شینه
 *    (مثلِ lib/prefs)، نه روی سرور: نه یه ستونِ تازه می‌خواد نه یه مسیرِ
 *    خطای تازه، و بدترین حالتش اینه که روی گوشیِ دومِ همون آدم یه‌بار دیگه
 *    دیده بشه.
 *
 * «دوست» این‌جا یعنی **هر کسی توی فهرستِ دوستان**: دوستیِ تاییدشده، درخواستِ
 * باز (هر دو طرف)، و مخاطبی که به‌عنوان دوست علامت خورده. نوعش مهم نیست؛
 * فهرست خالی باشه یا نه، همین.
 */
import { storage } from '../platform.ts';
import { recordPref } from './prefSink.ts';

/** یه کلید به‌ازای هر کاربر: یه دستگاه ممکنه چند حساب ببینه. */
const keyFor = (userId: string): string => `whosup.noFriendsNotice.v1.${userId}`;

/** «دیگه نپرس» — چه هشدار نشون داده شده باشه، چه معلوم شده باشه دوست داره. */
export async function markNoFriendsNoticeDone(userId: string): Promise<void> {
  // پرچم همچنان روی دستگاه است (دلیلش بالای فایل)؛ این فقط ثبت می‌کند که
  // این آدم یک‌بار به آن هشدار رسیده — عددی که می‌گوید چند نفر بدونِ دوست
  // به ساختنِ سیگنال رسیده‌اند.
  recordPref('noFriendsNotice', true);
  try {
    await storage().setItem(keyFor(userId), '1');
  } catch {
    // ذخیره نشد؛ بدترین حالت اینه که دفعه‌ی بعد یه‌بار دیگه پرسیده بشه.
  }
}

async function isDone(userId: string): Promise<boolean> {
  try {
    return (await storage().getItem(keyFor(userId))) === '1';
  } catch {
    return false;
  }
}

/**
 * چند نفر توی فهرستِ دوستانم‌اند — هر نوعش. فقط صفر/غیرصفرش مهمه.
 *
 * دو ریپازیتوری با `import()` و سرِ صحنه گرفته می‌شن، نه بالای فایل: این تابع
 * فقط یه‌بار در کلِ عمرِ حساب اجرا می‌شه، و ایمپورتِ ثابتش یعنی هر کسی که
 * این ماژول رو صدا می‌زنه — از جمله تستِ واحد — کلِ لایه‌ی داده و کلاینتِ
 * Supabase رو هم با خودش می‌کشه. مسیر ثابت و رشته‌ایه، پس هم Vite هم Metro
 * می‌شناسنش (همون قاعده‌ی i18n.ts).
 */
async function countPeople(): Promise<number> {
  const [{ friendRepo }, { contactFriendRepo }] = await Promise.all([
    import('../data/socialRepo.ts'),
    import('../data/contactFriendRepo.ts'),
  ]);
  const [friends, contacts] = await Promise.all([
    friendRepo.count(),
    contactFriendRepo.count(),
  ]);
  return friends + contacts;
}

/**
 * باید هشدار رو نشون بدیم؟ و اگه آره، همین‌جا هم علامت می‌زنه که پرسیده شده.
 *
 * ترتیبِ کار عمدیه: **اول** پرچمِ روی دستگاه، بعد شمردن. یعنی برای کسی که
 * یه‌بار جوابش رو داده، لمسِ «سیگنالِ جدید» هیچ رفت‌وبرگشتِ شبکه‌ای اضافه
 * نمی‌کنه و فرم بی‌معطلی باز می‌شه.
 *
 * و اگه معلوم شد دوست **داره**، همون‌جا علامت می‌خوره: جوابِ این سوال برای
 * اون حساب دیگه عوض نمی‌شه، پس شمردنِ دوباره‌ش با هر لمس بی‌فایده‌ست.
 *
 * شکستِ شبکه یعنی «نمی‌دونم» — نه هشدار، نه علامت. راه بازه و دفعه‌ی بعد
 * دوباره امتحان می‌شه؛ سدِ راهِ ساختِ سیگنال شدن به‌خاطرِ یه کوئریِ ناموفق
 * دقیقاً همون چیزیه که این هشدار قرار نبود باشه.
 *
 * `countMyPeople` فقط برای تست تزریق می‌شه؛ اپ‌ها بدونش صداش می‌زنن.
 */
export async function needsNoFriendsNotice(
  userId: string,
  countMyPeople: () => Promise<number> = countPeople,
): Promise<boolean> {
  if (!userId) return false;
  if (await isDone(userId)) return false;

  let people: number;
  try {
    people = await countMyPeople();
  } catch {
    return false;
  }

  await markNoFriendsNoticeDone(userId);
  return people === 0;
}
